import {
  PopoverArrowDown,
  PopoverArrowRight,
  PopoverArrowUp,
} from "@/components/Icon"
import React, { ReactNode, useEffect, useRef, useState } from "react"
import OutsideClickHandler from "react-outside-click-handler"
import Box, { BoxProps } from "./Box"
import Flex from "./Flex"
import Container from "./Container"
import { AnimatePresence, m } from "framer-motion"

type PopoverProps = {
  variant?: "default" | "container"
  content: ReactNode
  direction?: "trade" | "down" | "left" | "up"
  align?: "start" | "center" | "end"
  distance?: number
  offset?: number
  wrapperRef?: any
  isOpen: boolean
  onOutsideClick: (e: any) => void
} & BoxProps

export const Popover = ({
  variant = "default",
  content,
  direction = "down",
  children,
  align = "center",
  distance = 8,
  offset = 0,
  isOpen,
  onOutsideClick,
  zIndex = 90 /* Set it below header & footer, need to unified then. */,
  ...props
}: PopoverProps) => {
  const [translateX, setTranslateX] = useState(0)
  const [hasTranslated, setHasTranslated] = useState(false)
  const containerWrapperRef = useRef<any>()
  const contentWrapperRef = useRef<any>()

  const renderPopover = () => {
    const getAlignmentValue = () => {
      switch (align) {
        case "start":
          return "0%"
        case "end":
          return "100%"
        case "center":
        default:
          return "50%"
      }
    }

    switch (direction) {
      case "trade":
        return (
          <Flex
            position={["fixed", "fixed", "absolute"]}
            right={["50%", "50%", "100%"]}
            top={getAlignmentValue()}
            customCss={{
              transform: [
                `translateY(-50%) translateY(${offset}px) translateX(50%)`,
                `translateY(-50%) translateY(${offset}px) translateX(50%)`,
                `translateY(-50%) translateY(${offset}px)`,
              ],
            }}
            alignItems="center"
            zIndex={zIndex}
          >
            <Box
              bg="primaryPaleWhite"
              maxWidth="100vw"
              ref={contentWrapperRef}
              opacity={hasTranslated ? 1 : 0}
              borderRadius={4}
              overflow="hidden"
              customCss={{
                transform: `translateX(${translateX}px)`,
              }}
            >
              {content}
            </Box>
            <Box
              display={["none", "none", "flex"]}
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              width={8}
              mr={`${distance}px`}
              ml="-1px"
            >
              <PopoverArrowRight color="primaryPaleWhite" fontSize={27} />
            </Box>
          </Flex>
        )
      case "left":
        return (
          <Flex
            position="absolute"
            right="100%"
            top={getAlignmentValue()}
            customCss={{
              transform: `translateY(-50%) translateY(${offset}px)`,
            }}
            alignItems="center"
            zIndex={zIndex}
          >
            <Box
              bg="primaryPaleWhite"
              maxWidth="100vw"
              ref={contentWrapperRef}
              opacity={hasTranslated ? 1 : 0}
              borderRadius={4}
              overflow="hidden"
              customCss={{
                transform: `translateX(${translateX}px)`,
              }}
            >
              {content}
            </Box>
            <Flex
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              width={8}
              mr={`${distance}px`}
              ml="-1px"
            >
              <PopoverArrowRight color="primaryPaleWhite" fontSize={27} />
            </Flex>
          </Flex>
        )
      case "up":
      case "down":
      default:
        return (
          <>
            {/* An invisible div for container popover to adjust the translate x */}
            {variant === "container" && (
              <Box position="fixed" top={0} left={0} width="100vw">
                <Container p={0} ref={containerWrapperRef} />
              </Box>
            )}
            <Box
              position="absolute"
              top={direction === "down" ? "100%" : undefined}
              bottom={direction === "up" ? "100%" : undefined}
              left={getAlignmentValue()}
              customCss={{
                transform: `translateX(-50%) translateX(${offset}px)`,
              }}
              zIndex={zIndex}
            >
              {direction === "down" && (
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  height={8}
                  mt={`${distance}px`}
                  mb="-1px"
                >
                  <PopoverArrowUp color="primaryPaleWhite" fontSize={27} />
                </Flex>
              )}
              <Box
                {...(variant === "container" && {
                  width: "containerWidth",
                })}
                maxWidth="100vw"
                bg="primaryPaleWhite"
                borderRadius={4}
                ref={contentWrapperRef}
                opacity={hasTranslated ? 1 : 0}
                overflow="hidden"
                customCss={{
                  transform: `translateX(${translateX}px)`,
                }}
              >
                {content}
              </Box>
              {direction === "up" && (
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  height={8}
                  mt="-1px"
                  mb={`${distance}px`}
                >
                  <PopoverArrowDown color="primaryPaleWhite" fontSize={27} />
                </Flex>
              )}
            </Box>
          </>
        )
    }
  }

  useEffect(() => {
    if (!contentWrapperRef.current) return
    const contentWrapperEl = contentWrapperRef.current
    const contentWrapperRect = contentWrapperEl.getBoundingClientRect()
    let left = 0
    let right = window.innerWidth

    if (containerWrapperRef?.current) {
      const containerWrapperEl = containerWrapperRef.current
      const containerWrapperRect = containerWrapperEl.getBoundingClientRect()
      left = containerWrapperRect.left
      right = containerWrapperRect.right
    }

    if (contentWrapperRect.left < left) {
      setTranslateX(prev => prev + left - contentWrapperRect.left)
    } else if (contentWrapperRect.right > right) {
      setTranslateX(prev => prev + right - contentWrapperRect.right)
    }

    setHasTranslated(true)
  }, [children])

  return (
    <OutsideClickHandler
      onOutsideClick={onOutsideClick}
      disabled={!isOpen}
      display="flex"
    >
      <Box position="relative" display="inline-flex" {...props}>
        {children}
        <AnimatePresence>
          {isOpen && (
            <m.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {renderPopover()}
            </m.div>
          )}
        </AnimatePresence>
      </Box>
    </OutsideClickHandler>
  )
}
